import { Button, Input } from "@deligoo/ui"
import clsx from "clsx"
import dayjs from "dayjs"
import { ChangeEvent, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { shallowEqual } from "react-redux"
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer"
import { FixedSizeList } from "react-window"

import { useAppDispatch, useAppSelector } from "@/App"
import { Main } from "@/components/AppLayout"
import { Status } from "@/components/Status"
import { Table, ThProps } from "@/components/Table"
import { DriverContextMenu, DriverContextMenuAction, DriverItemContext } from "@/modules/DriverContextMenu"
import { clearGlobalDrivers, getDrivers, selectDriversStatus, selectSortedFilteredDriversIds } from "@/store/drivers"
import { selectTeamsIds } from "@/store/teams"
import { Driver, Sorter } from "@/types"
import { escapeRegex } from "@/utils"

import { DriversListRow } from "./DriversListRow"
import cls from "./DriversListView.module.scss"
import { sortDriversListColumn } from "./sortDriversListColumn"

const TABLE_HEADERS = [
  "ordinal",
  "name",
  "team_name",
  "phone",
  "email",
  "device",
  "payment_collection_system",
  "disabled_payment_forms",
  "working_hours",
  "blocks",
  "unsettled_orders_count",
  "transport",
  "status",
] as const

export type DriversTableHeader = typeof TABLE_HEADERS[number]

const TABLE_COLUMNS_WIDTHS: Record<DriversTableHeader | "context_menu", string> = {
  ordinal: "40px",
  name: "minmax(180px, 360px)",
  team_name: "minmax(160px, 320px)",
  phone: "180px",
  email: "minmax(180px, 360px)",
  device: "80px",
  payment_collection_system: "120px",
  disabled_payment_forms: "100px",
  working_hours: "160px",
  blocks: "120px",
  unsettled_orders_count: "110px",
  transport: "160px",
  status: "130px",
  context_menu: "minmax(70px, 1fr)",
}

const columns = Object.values(TABLE_COLUMNS_WIDTHS).join(" ")

export const DriversListView = () => {
  const { t } = useTranslation()

  const [isGlobal, setIsGlobal] = useState(false)

  const [searchValue, setSearchValue] = useState("")
  const [isSortedDesc, setIsSortedDesc] = useState(false)
  const [sorter, setSorter] = useState<Sorter<Driver>>(sortDriversListColumn("name", false))
  const [currentColumnSort, setCurrentColumnSort] = useState<DriversTableHeader | null>("name")

  const [driverContextMenuAction, setDriverContextMenuAction] = useState<DriverContextMenuAction>(null)

  const filterBySearch = useCallback(
    (driver: Driver) => {
      if (!searchValue) return true
      const searchValues = escapeRegex(searchValue).trim().split(" ")
      const regExps = searchValues.map((searchValue) => new RegExp(searchValue, "i"))
      return regExps.every(
        (regex) =>
          regex.test(driver.name) ||
          regex.test(driver.email) ||
          regex.test(driver.state) ||
          regex.test(String(driver.phone))
      )
    },
    [searchValue]
  )

  const driversStatus = useAppSelector(selectDriversStatus)
  const filteredDriversIds = useAppSelector(selectSortedFilteredDriversIds(sorter, filterBySearch), shallowEqual)
  const isTeamSelected = useAppSelector((state) => Boolean(state.teams.current))
  const teamsAvailableForUser = useAppSelector(selectTeamsIds) as number[]

  const dispatch = useAppDispatch()

  useEffect(() => {
    let abortGetDrivers: () => void = () => null

    if (isGlobal) {
      abortGetDrivers = dispatch(getDrivers({ teamIds: teamsAvailableForUser, date: dayjs().format("YYYY-MM-DD") }))
        .abort
    } else {
      dispatch(clearGlobalDrivers())
    }

    return () => {
      abortGetDrivers()
      dispatch(clearGlobalDrivers())
    }
  }, [dispatch, isGlobal, teamsAvailableForUser])

  if (!isTeamSelected) return <Status type="no_team" title={t("couriers")} fullHeight />

  function onColumnSort(column: DriversTableHeader) {
    const currentIsSortedDesc = column === currentColumnSort ? !isSortedDesc : false
    const columnSortCallback = sortDriversListColumn(column, currentIsSortedDesc)

    if (columnSortCallback) setSorter(columnSortCallback)

    setIsSortedDesc(currentIsSortedDesc)
    setCurrentColumnSort(column)
  }

  function getThSortedBy(column: DriversTableHeader): ThProps["sortedBy"] {
    if (currentColumnSort === column) {
      return isSortedDesc ? "desc" : "asc"
    }
  }

  return (
    <Main page pageContrast>
      <DriverItemContext.Provider
        value={{
          driverContextMenuAction,
          setDriverContextMenuAction,
        }}
      >
        <div className={cls.top}>
          <Input
            placeholder="Szukaj"
            iconRightProps={{ name: "search", color: "primary" }}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            plain
            dark
          />
          <Button className={cls.toggleGlobal} color="primary" onClick={() => setIsGlobal((isGlobal) => !isGlobal)}>
            {isGlobal ? "Wyłącz kurierzy globalnie" : "Kurierzy globalnie"}
          </Button>
        </div>
        <Table.Card>
          <Table
            minWidth="1800px"
            columns={columns}
            loading={driversStatus !== "fetched"}
            error={driversStatus === "error"}
            empty={filteredDriversIds.length === 0}
            virtualized
          >
            <Table.Thead>
              <Table.Tr>
                {TABLE_HEADERS.map((header) => (
                  <Table.Th
                    sortedBy={getThSortedBy(header)}
                    onClick={() => onColumnSort(header)}
                    className={clsx(header === "device" && "justify-center")}
                    key={header}
                  >
                    {t(`drivers_list_headers.${header}`)}
                  </Table.Th>
                ))}
                <Table.Th />
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              <AutoSizer>
                {({ height, width }) => (
                  <FixedSizeList
                    height={height}
                    width={width}
                    overscanCount={4}
                    itemData={filteredDriversIds}
                    itemCount={filteredDriversIds.length}
                    itemKey={(index, data) => data[index]}
                    itemSize={110}
                  >
                    {DriversListRow}
                  </FixedSizeList>
                )}
              </AutoSizer>
            </Table.Tbody>
          </Table>
        </Table.Card>

        <DriverContextMenu />
      </DriverItemContext.Provider>
    </Main>
  )
}
